import { StatusId, OperationBCI, CommandeRang } from '../types';

// Logique PURE des boutons acheteur-BC : visibilite + desactivation metier de
// chaque action, par onglet (consultation / offre). Aucune dependance React,
// aucun concept MS/prestataire, aucun magic number : tout passe par StatusId.
// Les noms de statut peuvent changer en DB, les ids non -> on gate sur l'id.
//
// Partage des responsabilites avec le composant :
//   - ce module decide VISIBLE + DISABLED METIER (+ une raison pour le tooltip)
//   - les flags ephemeres (isSaving / isDeleting / loading / updatingReference)
//     restent dans la feuille : `disabled={action.disabled || isSaving}`.
//   - l'evenement runtime `chronoExpired` est injecte via inputs.chronoExpired ;
//     l'expiration par statut/date, elle, est calculee ici.

export interface ActionState {
  visible: boolean;
  disabled: boolean;
  reason?: string;
}

const EMPTY: ActionState = { visible: false, disabled: false };

// --- Helpers purs -------------------------------------------------------------
const parseRang = (value: string | number): number => parseInt(String(value), 10);

const getCmd = (operation: OperationBCI, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

const isRefuseOuExpire = (idStatus?: number): boolean =>
  idStatus === StatusId.Refuse || idStatus === StatusId.Expire;

const cmdHasArticles = (cmd?: CommandeRang): boolean => (cmd?.articles?.length ?? 0) > 0;

const isBrouillon = (operation: OperationBCI): boolean =>
  operation.status?.id === StatusId.BrouillonAcheteur;

const isTransferred = (operation: OperationBCI): boolean => operation.transferred === true;

const isDateExpired = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const target = new Date(dateStr).getTime();
  return !Number.isNaN(target) && Date.now() >= target;
};

// Expiration telle que les boutons la voient : statut Expire/Annule, date depassee,
// ou evenement chrono (injecte). La feuille BDC est mono-source -> pas de garde `source`.
const isExpired = (operation: OperationBCI, chronoExpired: boolean): boolean =>
  chronoExpired ||
  operation.status?.id === StatusId.Expire ||
  operation.status?.id === StatusId.Annule ||
  isDateExpired(operation.expire_at);

// Le prestataire a renvoye une offre exploitable : articles presents, ou statut
// AttenteAnalyse(13) / AAccepter(4).
const prestataireAEnvoyeOffre = (cmd?: CommandeRang): boolean =>
  !!cmd &&
  (cmdHasArticles(cmd) ||
    cmd.id_status === StatusId.AttenteAnalyse ||
    cmd.id_status === StatusId.AAccepter);

const cmdAvecProposition = (cmd?: CommandeRang): boolean =>
  cmd?.id_status === StatusId.PropositionFaite;

// Statuts ou "Annuler" n'a plus de sens (la commande est figee) : Brouillon(1),
// Valide(7), Refuse(8), Expire(9), Annule(14).
const isTerminalPourAnnulation = (idStatus?: number): boolean =>
  idStatus === StatusId.BrouillonAcheteur ||
  idStatus === StatusId.Valide ||
  idStatus === StatusId.Refuse ||
  idStatus === StatusId.Expire ||
  idStatus === StatusId.Annule;

// CGV deja acquises sans case a cocher : panier inverse (transfere), ou rang>1
// debloque par un rang precedent refuse/expire en phase d'analyse.
const cgvDejaAcquises = (operation: OperationBCI, rang: number): boolean => {
  if (isTransferred(operation)) return true;
  const cmdPrec = getCmd(operation, rang - 1);
  return operation.status?.id === StatusId.AAnalyser && isRefuseOuExpire(cmdPrec?.id_status);
};

// Porte de decision de l'onglet Offre : conditions sous lesquelles l'acheteur peut
// agir sur l'offre du prestataire (accepter / refuser).
const peutDeciderSurOffre = (operation: OperationBCI, rang: number, expired: boolean): boolean => {
  if (expired) return false;
  const cmd = getCmd(operation, rang);
  const cmdPrec = getCmd(operation, rang - 1);
  const cmdRefuseOuExpire = isRefuseOuExpire(cmd?.id_status);

  if (isTransferred(operation)) {
    // Panier inverse : le prestataire a fait une proposition, statut AAccepter(4).
    return (
      operation.status?.id === StatusId.AAccepter &&
      !cmdRefuseOuExpire &&
      cmdAvecProposition(cmd)
    );
  }

  // Panier normal : phase AAnalyser(12).
  if (operation.status?.id === StatusId.AAnalyser) {
    if (rang === 1) {
      return !cmdRefuseOuExpire && prestataireAEnvoyeOffre(cmd);
    }
    return (
      isRefuseOuExpire(cmdPrec?.id_status) &&
      !!cmd &&
      cmd.id_status !== StatusId.BrouillonAcheteur &&
      !cmdRefuseOuExpire &&
      prestataireAEnvoyeOffre(cmd)
    );
  }

  return false;
};

// Edition de la consultation possible : brouillon acheteur et non expire. (Equivaut
// a `!isSectionReadOnly('admin')` cote feuille ; un transfert sort du statut brouillon.)
const consultationEditable = (operation: OperationBCI, expired: boolean): boolean =>
  !expired && isBrouillon(operation);

// --- Onglet Consultation ------------------------------------------------------
export interface ConsultationInputs {
  isSignataire: boolean;
  articlesMode: 'initial' | 'catalogue' | 'prestataire';
  hasArticlesPresent: boolean; // articles existants ou selectionnes
  chronoExpired?: boolean;
}

export interface ConsultationActions {
  ajouterArticle: ActionState;
  choisirCatalogue: ActionState;
  laisserPrestataire: ActionState;
  changerMode: ActionState;
  sauvegarderQuitter: ActionState;
  lancerConsultation: ActionState;
  demanderValidation: ActionState;
  accepterOffre: ActionState; // decision panier inverse surfacee dans l'onglet consultation
  refuserOffre: ActionState;
  consulterRangSuivant: ActionState;
  annuler: ActionState;
}

export const computeConsultationActions = (
  operation: OperationBCI,
  rang: number,
  inputs: ConsultationInputs,
): ConsultationActions => {
  const { isSignataire, articlesMode, hasArticlesPresent, chronoExpired = false } = inputs;
  const expired = isExpired(operation, chronoExpired);
  const edit = consultationEditable(operation, expired); // !expired && brouillon acheteur
  const sid = operation.status?.id;
  const cmd = getCmd(operation, rang);
  const cmdPrec = getCmd(operation, rang - 1);
  const modeInitialVide = articlesMode === 'initial' && !hasArticlesPresent;
  const modePrestataire = articlesMode === 'prestataire';

  // Decision panier inverse (offre du prestataire recue) : strictement AttenteAnalyse(13)
  // ou AAccepter(4) pour ce rang. Hors expiration.
  const offrePrestataireRecue =
    !expired &&
    isTransferred(operation) &&
    !!cmd &&
    (cmd.id_status === StatusId.AttenteAnalyse || cmd.id_status === StatusId.AAccepter);

  // Passage au rang suivant apres refus/expiration du precedent, rang encore en brouillon.
  const peutPasserRangSuivant =
    !expired &&
    isRefuseOuExpire(cmdPrec?.id_status) &&
    rang < 4 &&
    sid === StatusId.AAnalyser &&
    cmd?.id_status === StatusId.BrouillonAcheteur;

  const lancementBloque = modePrestataire
    ? 'Mode prestataire : la consultation se lance apres reception des articles'
    : undefined;

  // "Obtenir la validation" (non-signataire) apparait en mode edition (lancement) ET
  // au passage de rang. Le mode prestataire ne le bloque qu'en mode edition.
  const demanderValidationVisible = (edit || peutPasserRangSuivant) && !isSignataire;

  return {
    ajouterArticle: { visible: edit, disabled: false },
    choisirCatalogue: { visible: edit && modeInitialVide, disabled: false },
    laisserPrestataire: { visible: edit && modeInitialVide, disabled: false },
    changerMode: { visible: edit, disabled: false },
    sauvegarderQuitter: { visible: edit, disabled: false },
    lancerConsultation: {
      visible: edit && isSignataire,
      disabled: modePrestataire,
      reason: lancementBloque,
    },
    demanderValidation: {
      visible: demanderValidationVisible,
      disabled: edit ? modePrestataire : false,
      reason: edit ? lancementBloque : undefined,
    },
    accepterOffre: { visible: offrePrestataireRecue, disabled: false },
    refuserOffre: { visible: offrePrestataireRecue, disabled: false },
    consulterRangSuivant: { visible: peutPasserRangSuivant && isSignataire, disabled: false },
    annuler: {
      // Tous les contextes "Annuler" (edition, panier inverse, passage de rang, repli
      // non terminal), masque si expire.
      visible:
        !expired &&
        (edit || offrePrestataireRecue || peutPasserRangSuivant || !isTerminalPourAnnulation(sid)),
      disabled: false,
    },
  };
};

// --- Onglet Offre -------------------------------------------------------------
// Les CGV ne desactivent PAS le bouton "Accepter" : elles sont une etape du flux
// (clic -> popup CGV -> signature), pilotee dans la feuille. Le domaine ne decide
// donc que la visibilite ; le bouton n'est bloque que par le `loading` ephemere.
export interface OffreInputs {
  isSignataire: boolean;
  chronoExpired?: boolean;
}

export interface OffreActions {
  accepterOffre: ActionState;
  refuserOffre: ActionState;
  demanderValidation: ActionState;
  annuler: ActionState;
  telechargerBdc: ActionState;
  modifierReference: ActionState;
}

export const computeOffreActions = (
  operation: OperationBCI,
  rang: number,
  inputs: OffreInputs,
): OffreActions => {
  const { isSignataire, chronoExpired = false } = inputs;
  const expired = isExpired(operation, chronoExpired);
  const sid = operation.status?.id;
  const cmd = getCmd(operation, rang);
  const decision = peutDeciderSurOffre(operation, rang, expired);

  return {
    accepterOffre: {
      visible: decision && isSignataire,
      disabled: false,
    },
    refuserOffre: { visible: decision, disabled: false },
    demanderValidation: { visible: decision && !isSignataire, disabled: false },
    annuler: {
      // Decision en cours, ou repli "non terminal" hors fenetre de decision.
      visible: decision || (!expired && !isTerminalPourAnnulation(sid)),
      disabled: false,
    },
    telechargerBdc: { visible: cmd?.id_status === StatusId.Valide, disabled: false },
    modifierReference: {
      visible:
        isTransferred(operation) &&
        operation.status?.id === StatusId.AAccepter &&
        !isRefuseOuExpire(cmd?.id_status) &&
        cmdAvecProposition(cmd),
      disabled: false,
    },
  };
};

// Expose pour reutilisation/tests cibles (memes regles que ci-dessus).
export const operationActionsInternals = {
  isExpired,
  peutDeciderSurOffre,
  cgvDejaAcquises,
  prestataireAEnvoyeOffre,
  isTerminalPourAnnulation,
  EMPTY,
};
